import axios from 'axios';
import CryptoJS from 'crypto-js';

/**
 * Veriff API utility for AML screening
 * Handles session creation, submission, and result fetching with HMAC authentication
 */

// Production API Key with AML enabled
const API_KEY = process.env.REACT_APP_VERIFF_API_KEY || '69ed80ab-9f87-41b2-9120-74b2ddb10a3d';
const SHARED_SECRET = process.env.REACT_APP_VERIFF_SHARED_SECRET || 'c5899692-5e79-4083-868c-e89aa86f7407';

/**
 * Generate HMAC-SHA256 signature
 * @param {string} payload - The payload to sign (request body for POST/PATCH, sessionId for GET)
 */
const generateSignature = (payload) => {
    return CryptoJS.HmacSHA256(payload, SHARED_SECRET).toString(CryptoJS.enc.Hex);
};

/**
 * Make API request to Veriff
 */
export const fetchVeriffApi = (env, endpoint, method, data = null) => {
    method = method.toUpperCase();

    // Call Veriff API directly
    const baseUrl = process.env.REACT_APP_PRODUCTION_VERIFF_API_URL || 'https://stationapi.veriff.com';

    const headers = {
        'x-auth-client': API_KEY,
        'Content-Type': 'application/json'
    };

    // Add HMAC signature for requests that need it (PATCH, GET with sessionId)
    if (method === 'PATCH' || method === 'PUT') {
        const payload = data ? JSON.stringify(data) : '';
        headers['x-hmac-signature'] = generateSignature(payload);
    }

    console.log('Veriff API Request:', {
        env,
        baseUrl,
        endpoint,
        method,
        hasSignature: !!headers['x-hmac-signature']
    });

    const config = {
        method: method,
        baseURL: baseUrl,
        url: endpoint,
        headers,
        timeout: 30000
    };

    if (data && (method === 'POST' || method === 'PATCH' || method === 'PUT')) {
        config.data = data;
    }

    return axios(config);
};

/**
 * Make GET request with HMAC signature based on sessionId
 */
export const fetchVeriffApiWithSessionSignature = (env, endpoint, sessionId) => {
    const isDevelopment = process.env.NODE_ENV === 'development';
    let baseUrl;

    // Call Veriff API directly
    baseUrl = process.env.REACT_APP_PRODUCTION_VERIFF_API_URL || 'https://stationapi.veriff.com';

    const headers = {
        'x-auth-client': API_KEY,
        'x-hmac-signature': generateSignature(sessionId),
        'Content-Type': 'application/json'
    };

    console.log('Veriff API GET Request:', { baseUrl, endpoint, sessionId: sessionId.substring(0, 8) + '...' });

    return axios({
        method: 'GET',
        baseURL: baseUrl,
        url: endpoint,
        headers,
        timeout: 30000
    });
};

/**
 * Create a new AML screening session
 */
export const createSession = (env, personData) => {
    const payload = {
        verification: {
            person: {
                firstName: personData.firstName,
                lastName: personData.lastName
            }
        }
    };

    if (personData.dateOfBirth) {
        payload.verification.person.dateOfBirth = personData.dateOfBirth;
    }

    return fetchVeriffApi(env, '/v1/sessions', 'POST', payload);
};

/**
 * Submit a session for screening
 */
export const submitSession = (env, sessionId) => {
    const payload = {
        verification: {
            status: 'submitted'
        }
    };

    return fetchVeriffApi(env, `/v1/sessions/${sessionId}`, 'PATCH', payload);
};

/**
 * Get watchlist screening results
 */
export const getScreeningResults = (env, sessionId) => {
    return fetchVeriffApiWithSessionSignature(env, `/v1/sessions/${sessionId}/watchlist-screening`, sessionId);
};

/**
 * Perform full AML screening flow
 */
export const performFullScreening = async (env, personData, maxAttempts = 10, pollInterval = 2000) => {
    console.log('Starting AML screening for:', personData.firstName, personData.lastName);

    // Step 1: Create session
    const createResponse = await createSession(env, personData);
    const sessionId = createResponse.data?.verification?.id;

    if (!sessionId) {
        throw new Error('Failed to create session: No session ID returned');
    }
    console.log('Session created:', sessionId);

    // Step 2: Submit session
    await submitSession(env, sessionId);
    console.log('Session submitted');

    // Step 3: Poll for results
    let attempts = 0;
    while (attempts < maxAttempts) {
        try {
            const resultsResponse = await getScreeningResults(env, sessionId);
            const results = resultsResponse.data;

            if (results && results.data && results.data.matchStatus) {
                console.log('Screening complete:', results.data.matchStatus);
                return {
                    sessionId,
                    matchStatus: results.data.matchStatus,
                    hits: results.data.hits || [],
                    screenedAt: new Date().toISOString()
                };
            }
        } catch (error) {
            if (error.response?.status === 404) {
                console.log(`Polling attempt ${attempts + 1}/${maxAttempts} - results not ready`);
            } else {
                console.warn('Polling error:', error.message);
            }
        }

        attempts++;
        if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
    }

    throw new Error('AML screening not enabled or results not available. Please check your Veriff integration settings.');
};
